import React, { useCallback, useContext, useEffect, useState } from 'react';
import { GoogleMap, LoadScript, Marker, Polygon, Polyline } from '@react-google-maps/api';

import styles from './MapEditor.module.scss';
import { Area, EditedArea } from '../../types/types';
import { AreaStatus, ButtonType, SnackbarType } from '../../types/enums';
import { convertPointsToLatLng } from '../../utils/common';
import {COMMON_POLYGON_OPTIONS, GOOGLE_MAPS_VERSION, POLYGON_OPTIONS_BY_STATUS} from '../Map/Map';
import Button from '../Button/Button';
import deleteIcon from './map-delete.svg';
import databaseIcon from './database-server.svg';
import { updateMapAreas } from '../../requests';
import { NotificationContext } from '../../App';

interface MapEditorProps {
  areas: Area[];
  closeMapEditor: () => void;
}

const containerStyle = {
  width: '100%',
  height: '100%'
};

const center = {
  lat: 47.503,
  lng: 19.067
};

const MapEditor = ({ areas, closeMapEditor }: MapEditorProps) => {
  const notify = useContext(NotificationContext);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawPoints, setDrawPoints] = useState<google.maps.LatLngLiteral[]>([]);
  const [transformedAreas, setTransformedAreas] = useState<EditedArea[]>(() =>
    areas.map(formatArea)
  );
  const [editableAreas, setEditableAreas] = useState<Record<string, boolean>>({});
  const [deletedAreaIds, setDeletedAreaIds] = useState<{ id: number; new: boolean }[]>([]);
  const polygonRefs = React.useRef<Record<string, google.maps.Polygon>>({});
  const drawLineRef = React.useRef<google.maps.Polyline | null>(null);
  const threshold = 0.0002; // Snap distance

  const closeEditor = () => {
    closeMapEditor();
  };

  const onSuccess = useCallback(() => {
    notify(
      'A térkép mentése sikeres volt',
      SnackbarType.SUCCESS
    );
    closeEditor();
  }, [notify]);

  const onError = useCallback(() => {
    notify(
      'A térkép mentése nem sikerült.',
      SnackbarType.ERROR
    );
  }, [notify]);

  const saveAreas = useCallback(() => {
    updateMapAreas({ areas: transformedAreas, deletedAreas: deletedAreaIds }, onSuccess, onError).then();
  }, [transformedAreas, deletedAreaIds]);

  const toggleEditable = (areaId: string) => {
    setEditableAreas((prev) => ({
      ...prev,
      [areaId]: !prev[areaId]
    }));
  };

  const snapVertex = (
    movedPoint: google.maps.LatLngLiteral,
    movingPolygonId: string
  ): google.maps.LatLngLiteral | null => {
    let closestPoint: google.maps.LatLngLiteral | null = null;
    let minDistance = threshold;

    Object.entries(polygonRefs.current).forEach(([id, polygon]) => {
      if (id === movingPolygonId) return; // Skip the moving polygon

      const path = polygon.getPath();
      path.forEach((point) => {
        const otherPoint: google.maps.LatLngLiteral = { lat: point.lat(), lng: point.lng() };
        const distance = getDistance(movedPoint, otherPoint);

        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = otherPoint;
        }
      });
    });

    return closestPoint;
  };

  const addPathListeners = (polygon: google.maps.Polygon, areaId: string) => {
    const path = polygon.getPath();
    let skipEvent = false;

    const handleVertexChange = (index: number) => {
      if (skipEvent) return;

      const movedPoint = path.getAt(index).toJSON() as google.maps.LatLngLiteral;
      const snapPoint = snapVertex(movedPoint, areaId);

      if (snapPoint) {
        skipEvent = true;
        path.setAt(index, new google.maps.LatLng(snapPoint.lat, snapPoint.lng));
        skipEvent = false;
      }

      // Get updated path as geojson
      const updatedGeojson = path.getArray().map(point => (
        [
          point.lat(),
          point.lng()
        ]
      ));

      // Update transformedAreas with new path and mark as modified
      setTransformedAreas((prev) =>
        prev.map((area) =>
          area.id.toString() === areaId
            ? { ...area, storedGeoJson: JSON.stringify(updatedGeojson), modified: true }
            : area
        )
      );
    };


    path.addListener('set_at', (index: number) => handleVertexChange(index));
    path.addListener('insert_at', (index: number) => handleVertexChange(index));
  };

  const completeNewArea = (points: google.maps.LatLngLiteral[]) => {
    setTimeout(() => {
      const areaCode = prompt('Az új terület kódja (pl. "BPXY1"):');
      if (!areaCode) {
        return;
      }

      const storedGeoJson = points.map(point => (
        [
          point.lat,
          point.lng
        ]
      ));

      const newArea: EditedArea = {
        id: Date.now(), // Use a unique ID
        code: areaCode,
        status: AreaStatus.FREE,
        geojson: points,
        storedGeoJson: JSON.stringify(storedGeoJson),
        new: true,
        modified: true
      };

      setTransformedAreas((prev) => [...prev, newArea]);
      setEditableAreas((prev) => ({ ...prev, [newArea.id]: true }));
    }, 100);
  };

  const startDrawing = () => {
    setDrawPoints([]);
    setIsDrawing(true);
  };

  const cancelDrawing = () => {
    setDrawPoints([]);
    setIsDrawing(false);
  };

  const finishDrawing = () => {
    if (drawPoints.length < 3) {
      notify('Egy területhez legalább 3 pont szükséges.', SnackbarType.ERROR);
      return;
    }
    const points = drawPoints;
    cancelDrawing();
    completeNewArea(points);
  };

  const addDrawPoint = (e: google.maps.MapMouseEvent) => {
    if (!isDrawing || !e.latLng) return;

    const clickedPoint = e.latLng.toJSON();
    const point = snapVertex(clickedPoint, 'new') ?? clickedPoint;

    setDrawPoints((prev) => {
      const last = prev[prev.length - 1];
      // Double click fires two clicks on the same spot, skip the duplicate
      if (last && getDistance(last, point) < threshold / 10) return prev;
      return [...prev, point];
    });
  };

  const updateDrawPreview = (e: google.maps.MapMouseEvent) => {
    if (!isDrawing || !e.latLng || !drawLineRef.current) return;
    // Set the rubber band line directly to avoid re-rendering every polygon on mouse move
    drawLineRef.current.setPath([...drawPoints, e.latLng.toJSON()]);
  };

  useEffect(() => {
    if (!isDrawing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelDrawing();
      if (e.key === 'Enter') finishDrawing();
      if (e.key === 'Backspace') setDrawPoints((prev) => prev.slice(0, -1));
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawing, drawPoints]);

  const deleteArea = (areaId: number) => {
    const areaToDelete = transformedAreas.find(area => area.id === areaId);
    if (window.confirm('Egészen biztosan törölhető ez a terület? (' + areaToDelete?.code + ')')) {
      setTransformedAreas((prev) => prev.filter((area) => area.id !== areaId));
      setDeletedAreaIds((prev) => [...prev, { id: areaId, new: areaToDelete?.new || false }]);

      if (polygonRefs.current[areaId]) {
        polygonRefs.current[areaId].setMap(null);
        delete polygonRefs.current[areaId];
      }
    }
  };

  return (
    <div className={styles.mapEditor}>
      <div className={styles.mapWrapper}>
        <div className={styles.mapCanvas} id="map-canvas" />
        <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY ?? ''} version={GOOGLE_MAPS_VERSION}>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={14}
            clickableIcons={false}
            options={{
              gestureHandling: 'greedy',
              disableDoubleClickZoom: isDrawing,
              draggableCursor: isDrawing ? 'crosshair' : undefined
            }}
            onClick={addDrawPoint}
            onDblClick={() => isDrawing && finishDrawing()}
            onMouseMove={updateDrawPreview}
          >
            {isDrawing && (
              <Polyline
                path={drawPoints}
                options={DRAW_LINE_OPTIONS}
                onLoad={(line) => { drawLineRef.current = line; }}
                onUnmount={() => { drawLineRef.current = null; }}
              />
            )}
            {isDrawing && drawPoints.length > 0 && (
              <Marker
                position={drawPoints[0]}
                title="Terület lezárása"
                onClick={finishDrawing}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 7,
                  fillColor: '#ffffff',
                  fillOpacity: 1,
                  strokeColor: DRAW_LINE_OPTIONS.strokeColor,
                  strokeWeight: 2
                }}
              />
            )}
            {transformedAreas.map((area) => {
              if(area.geojson[0].lat !== 0) {
                return (
                  <Polygon
                    key={area.id}
                    onLoad={(polygon) => {
                      polygonRefs.current[area.id] = polygon;
                      addPathListeners(polygon, area.id.toString());
                    }}
                    onClick={() => toggleEditable(area.id.toString())}
                    paths={area.geojson}
                    options={{
                      ...getAreaOptions(area.status, editableAreas[area.id] ?? false),
                      // While drawing, clicks must reach the map
                      clickable: !isDrawing
                    }}
                  />
                )
              }
            })}
          </GoogleMap>
        </LoadScript>
      </div>
      <div className={styles.drawPanel}>
        {isDrawing ? (
          <>
            <div className={styles.drawHint}>
              Kattints a pontok lerakásához. Lezárás: első pontra kattintás, dupla kattintás vagy Enter.
              Utolsó pont törlése: Backspace, mégse: Esc.
            </div>
            <Button action={finishDrawing} type={ButtonType.PRIMARY}>Kész</Button>
            <Button action={cancelDrawing} type={ButtonType.SECONDARY}>Mégse</Button>
          </>
        ) : (
          <Button action={startDrawing} type={ButtonType.PRIMARY}>Új terület</Button>
        )}
      </div>
      <div className={styles.controlPanel}>
        <div className={styles.panelTitle}>TERÜLETEK</div>
        <div className={styles.listWrapper}>
          {transformedAreas.map((area) => {
            if(area.geojson[0].lat !== 0) {
              return(
                <div className={styles.areaLink} key={area.id}>
                  <span onClick={() => toggleEditable(area.id.toString())}>
                    {editableAreas[area.id] ? <strong>{area.code}</strong> : area.code}
                  </span>
                  {area.modified ? <img src={databaseIcon} className={styles.databaseIcon} alt={"*"} /> : null}
                  <img src={deleteIcon} onClick={() => deleteArea(area.id)} className={styles.deleteButton} alt={"X"} />
                </div>
              )
            }
          })}
        </div>
        <div className={styles.saveButton}>
          <Button action={saveAreas} type={ButtonType.PRIMARY}>Mentés</Button>
        </div>
      </div>
      <div className={styles.closePanel}>
        <Button action={closeEditor} type={ButtonType.SECONDARY}>Bezárás</Button>
      </div>
    </div>
  );
};

const formatArea = (area: Area): EditedArea => ({
  ...area,
  geojson: convertPointsToLatLng(JSON.parse(area.geojson)),
  storedGeoJson: '',
  new: false,
  modified: false,
  office_id: undefined,
  numOfEstates: undefined,
  office: undefined
});

const getAreaOptions = (status: AreaStatus, active: boolean) => ({
  ...COMMON_POLYGON_OPTIONS,
  ...POLYGON_OPTIONS_BY_STATUS[status],
  editable: active,
  //suppressUndo: true
});

const DRAW_LINE_OPTIONS = {
  strokeColor: POLYGON_OPTIONS_BY_STATUS[AreaStatus.FREE].strokeColor,
  strokeOpacity: 1,
  strokeWeight: 2,
  clickable: false,
  zIndex: 2
};

const getDistance = (p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral) => {
  return Math.sqrt((p1.lat - p2.lat) ** 2 + (p1.lng - p2.lng) ** 2);
};

export default React.memo(MapEditor);
